// Generic horizontal chip slider (size-filter bar, PLP related collections…):
// the track overflows with native scroll/swipe, and "previous"/"next" arrows
// (each shown only while there is more to reveal on that side) nudge it along.
// Plain custom element — no framework coupling — so it re-initialises cleanly
// every time the enclosing section re-renders.

const SCROLL_RATIO = 0.8;

class ChipSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-chip-slider-track]');
    this.prevButton = this.querySelector('[data-chip-slider-prev]');
    this.nextButton = this.querySelector('[data-chip-slider-next]');
    if (!this.track) return;

    this.prevButton?.addEventListener('click', this.scrollPrev);
    this.nextButton?.addEventListener('click', this.scrollNext);
    this.track.addEventListener('scroll', this.update, { passive: true });

    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);

    // The section re-renders via morph on every filter change, which reuses this
    // element (so connectedCallback won't fire again) and restores the server-side
    // `hidden` attribute on the arrows. The track's width doesn't change, so the
    // ResizeObserver stays silent — watch the pills mutating instead and re-run
    // update() to recompute arrow visibility. Observing only the track (not the
    // arrows) keeps our own `hidden` toggles from re-triggering this.
    this.mutationObserver = new MutationObserver(() => requestAnimationFrame(this.update));
    this.mutationObserver.observe(this.track, { childList: true, subtree: true, attributes: true });

    this.update();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.scrollPrev);
    this.nextButton?.removeEventListener('click', this.scrollNext);
    this.track?.removeEventListener('scroll', this.update);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  // direction: -1 = previous, 1 = next
  step(direction) {
    this.track.scrollBy({ left: this.track.clientWidth * SCROLL_RATIO * direction, behavior: 'smooth' });
  }

  scrollPrev = () => this.step(-1);
  scrollNext = () => this.step(1);

  // Two ways to treat an arrow with nothing left to reveal: hide it, so the row
  // closes up around it (the default — what the size-filter bar and the PLP
  // related collections want), or leave it in place and disable it, for sliders
  // whose arrows sit in a fixed header and would leave a hole. Opt into the
  // latter with `data-chip-slider-arrows="disable"`.
  setArrowState(button, spent) {
    if (!button) return;

    const attribute = this.getAttribute('data-chip-slider-arrows') === 'disable' ? 'disabled' : 'hidden';
    button.toggleAttribute(attribute, spent);
  }

  update = () => {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // 1px tolerance absorbs sub-pixel rounding at the extremes.
    const hasOverflow = scrollWidth - clientWidth > 1;
    this.setArrowState(this.prevButton, !hasOverflow || scrollLeft <= 1);
    this.setArrowState(this.nextButton, !hasOverflow || scrollLeft + clientWidth >= scrollWidth - 1);
  };
}

if (!customElements.get('chip-slider')) {
  customElements.define('chip-slider', ChipSlider);
}
